import axios from 'axios';
import extend from 'lodash-es/extend';
import isPlainObject from 'lodash-es/isPlainObject';

import { hedging as retryHedging, TimeoutError } from '@tencent/retry-hedging';

import { getCookie } from './cookie';
import env from './env';
import logger from './logger';
import { buildCSRF, isNewQQLogin } from './login';
import { globalContext, globalObject } from './system';
import { buildQueryStr, buildUrl } from './url';
import { hasOwn } from './util';

export const RequestErrorType = {
  NETWORK_ERR: 'NETWORK_ERR',
  SYSTEM_ERR: 'SYSTEM_ERR',
  LOGIC_ERR: 'LOGIC_ERR',
};

const IS_STD_PROTOCOL = /^(http|https):$/.test(globalObject.location?.protocol);
const HTTP2HTTP_PROXY = 'https://access.video.qq.com/trans/';
const HTTP2HTTP_APPID = '68106135';
const HTTP2HTTP_SECRETKEY = 'e667570eb833960cc41051d498df1c233308eb195dba2cc3';
const METHODS = getRequestMethods();

const WUJI_REGEX = /(\/\/v\.qq\.com\/cache\/wuji)|(\/\/cache\.wuji\.qq\.com\/x\/api\/wuji_cache)|(\/\/node\.video\.qq\.com\/x\/api\/wuji_cache)|(\/\/api\.wuji\.qq\.com\/x\/api\/wuji_cache)/;
const WUJI_CDN_REGEX = /(\/\/v\.qq\.com\/cache\/wuji\/)|(\/\/cache\.wuji\.qq\.com\/x\/api\/wuji_cache\/)/;

function getRequestMethods() {
  const requestor = env.isCSR ? globalContext.request : axios;
  return new Map([
    ['get', async (url, params, options) => {
      await procHeaders(options);
      return requestor(extend({ url, params, method: 'get' }, options));
    }],
    ['post', async (url, data, options) => {
      await procHeaders(options);
      return requestor(extend({ url, data, method: 'post' }, options));
    }],
  ]);
}

async function procHeaders(options) {
  if (!options?.withCredentials || !env.isHoneWeb || typeof HoneJSCoreJSBridge === 'undefined') {
    return options;
  }

  // JSCore环境中，需要手动传cookie到请求头中
  try {
    const cookie = await getCookie();
    options.headers = options.headers || {};
    options.headers.Cookie = cookie;
  } catch (e) {
    logger.error('procHeaders error', e);
  }
  return options;
}

/**
 * 通用的请求错误描述类
 * @param code
 * @param msg
 * @param res
 * @constructor
 */
export function RequestError(code, msg, res, errorType) {
  this.code = code;
  this.msg = msg;
  this.res = res;
  this.errorType = errorType || RequestErrorType.LOGIC_ERR;
}

RequestError.prototype.toString = function () {
  return this.msg + (this.code ? `(${this.code})` : '');
};

RequestError.prototype.RequestErrorType = RequestErrorType;
RequestError.RequestErrorType = RequestErrorType;

/**
 * 通用请求
 * @param url
 * @param data
 * @param {Object} options axios的其他option 如 timeout 具体查看 https://github.com/axios/axios#axios-api
 * @param {boolean} [options.hedging = true] 是否开启对冲（仅支持无极 CDN 请求）
 * @param {boolean} [options.hedgingReport = false] 是否开启对冲结果上报
 * @return Promise
 */
export async function request(url, data = {}, options = {}) {
  if (isNewQQLogin() && url.match(/\/\/pay.video.qq.com/)) { // QQ互联迁移
    url = url.replace(/\/\/(pay.video.qq.com)/, '//access.video.qq.com/trans/$1');
    if (!isPlainObject(data)) {
      data = {};
    }
    const csrfParam = buildCSRF();
    extend(data, {
      vappid: HTTP2HTTP_APPID,
      vsecret: HTTP2HTTP_SECRETKEY,
    }, csrfParam);
  }
  if (!IS_STD_PROTOCOL && /^\/\//.test(url)) {
    url = `https:${url}`;
  }
  const { type, needCSRF, hedging = true, hedgingReport = false, ...others } = options;

  const method = (type || 'get').trim().toLowerCase();

  // 由于 axios 不支持 jsonp，需确保 url 参数带上 otype=xjson
  // 自动拼接 otype=xjson，以确保接口返回 json 对象数据
  if (
    url.match(/\/\/(pay|vip|hwvip)\.((video\.qq\.com)|(wetv\.vip))\//)
    || url.match(/\/_tde_\/vip_video_qq_com\/_tde_\//)
  ) {
    if (url.indexOf('otype=') < 0) {
      url += `${url.indexOf('?') > 0 ? '&' : '?'}otype=xjson`;
    } else {
      url = url.replace('otype=json', 'otype=xjson');
    }
  }

  // 离线包场景或传了needCSRF=true,通过query方式传递
  if (!IS_STD_PROTOCOL || needCSRF) {
    const csrfParam = buildCSRF();
    url = buildUrl(url, csrfParam);
  }

  if (method === 'post') {
    if (isPlainObject(data)) {
      // 兼容zepto的参数处理方式（如果要传json字符串，可以先调JSON.stringify(data)再传参；不建议直接传object，会触发预检，产生两次请求）
      data = buildQueryStr(data);
    }
  }

  // 用于请求携带cookie，如果没有设置withCredentials，则默认为true
  if (!hasOwn(options, 'withCredentials')) {
    others.withCredentials = !WUJI_REGEX.test(url);
  }

  const baseRequest = async (url) => {
    try {
      const res = await METHODS.get(method).call(this, url, data, others);
      if (res && res.status === 200 && res.data) {
        return res.data;
      }

      throw new RequestError(
        0,
        '服务器开小差了！请重试',
        res,
        RequestErrorType.NETWORK_ERR,
      );
    } catch (e) {
      if (e instanceof RequestError) {
        throw e;
      }

      throw new RequestError(
        0,
        '网络开小差了！请重试',
        e,
        RequestErrorType.NETWORK_ERR,
      );
    }
  };

  if (hedging && WUJI_CDN_REGEX.test(url)) {
    try {
      const accessUrl = url.replace(WUJI_CDN_REGEX, '//api.wuji.qq.com/x/api/wuji_cache/');
      const hedgingOptions = {
        timeout: options.timeout,
        report: hedgingReport,
      };
      return await retryHedging([
        () => baseRequest(url),
        () => baseRequest(accessUrl),
      ], hedgingOptions);
    } catch (e) {
      if (e instanceof TimeoutError) {
        throw new RequestError(
          0,
          '网络开小差了！请重试',
          e,
          RequestErrorType.NETWORK_ERR,
        );
      }
      throw e;
    }
  }
  return baseRequest(url);
}

export function nRequest(url, ...args) {
  return request(_formatNodeAPIUrl(url, args), null, {
    dataType: 'json',
  }).then(({ ret, msg, data }) => {
    if (ret !== 0) {
      throw new RequestError(ret, msg, null, RequestErrorType.LOGIC_ERR);
    }
    return data;
  });
}

function _formatNodeAPIUrl(url, args) {
  if (!args.length) {
    return url;
  }
  let i = 0;
  return url.replace(/%(s|n)/g, (m) => {
    let v = args[i];
    i += 1;
    if (v === undefined || v === null) {
      v = '';
    }
    switch (m) {
      case '%n':
        return v;
      case '%s':
      default:
        return `"${v}"`;
    }
  });
}

export function http2http(url, params = {}, options = {}) {
  return request(`${HTTP2HTTP_PROXY}${url.replace(/^(https:|http:)?\/\//, '')}`, {
    ...params,
    vappid: HTTP2HTTP_APPID,
    vsecret: HTTP2HTTP_SECRETKEY,
    ...buildCSRF(),
  }, options);
}

export function commConfig(configId, params = {}, options = {}) {
  return commCGI(
    { name: 'spp_get_confi', cmd: 1 },
    {
      config_id: configId,
      ...params,
    }, options,
  );
}
export function commCGI({ name, cmd }, params = {}, options = {}) {
  const isJson = options.dataType && options.dataType.toLowerCase() === 'json';
  if (options.withCredentials === true) {
    options = {
      xhrFields: { withCredentials: true },
      ...options,
    };
  }

  return request(`https://vip.video.qq.com/fcgi-bin/comm_cgi?name=${name}&cmd=${cmd}&otype=${isJson ? 'xjson' : 'json'}`, params, options).then((res) => {
    if (res && res.ret === -10) {
      throw new RequestError(res.ret, `CommCGI名称未配置(name=${name})`, res, RequestErrorType.SYSTEM_ERR);
    }
    return res;
  });
}

/**
 * rpcCGI 支持调用 rpcGo 的接口
 * @param {string} path rpc路由
 * @param {object} data 参数
 * @param {object} options 请求设置
 * @param {Boolean} options.strict 是否启用严格模式，启用后将严格按照接口规范，通过 response header trpc-func-ret 字段是否等于0判断请求是否成功，而不关心 body。
 * 该参数主要是为了修复并兼容低版本——如果body为空即认为请求失败——的问题
 * @example
 * // 请求接口 http://vip.video.qq.com/rpc/trpc.kidsvip.checkinfo.KidsVipService/KidsVipCheckInfo
 * try {
 *   await rpcCGI('trpc.kidsvip.checkinfo.KidsVipService/KidsVipCheckInfo')
 * } catch (e) {
 * }
 */
export async function rpcCGI(path = '', data = {}, options = {}) {
  const { type, needCSRF, strict, ...others } = options;
  const method = (type || 'get').trim().toLowerCase();
  others.withCredentials = true;

  if (method === 'post') {
    others.headers = {
      'Content-Type': 'text/plain;charset=utf-8',
    };
    if (isPlainObject(data)) {
      data = JSON.stringify(data);
    }
  } else if (method === 'get' && isPlainObject(data) && !data.rpc_data) {
    // 兼容处理业务自己透传的 rpc_data 参数，不再处理
    data = {
      rpc_data: JSON.stringify(data),
    };
  }

  let url = `//vip.video.qq.com/rpc/${path}`;
  if (path.match(/^(https?:)?\/\//)) {
    url = path;
  }
  if (!IS_STD_PROTOCOL && /^\/\//.test(url)) {
    url = `https:${url}`;
  }
  // 离线包场景或传了needCSRF=true,通过query方式传递
  if (!IS_STD_PROTOCOL || needCSRF) {
    const csrfParam = buildCSRF();
    url = buildUrl(url, csrfParam);
  }
  try {
    const res = await METHODS.get(method).call(this, url, data, others);

    if (res.status !== 200) {
      throw new RequestError(
        res.status,
        '网络请求失败',
        res,
        RequestErrorType.SYSTEM_ERR,
      );
    }

    if (
      // 严格模式遵循接口规范，仅根据 header 字段 trpc-func-ret 和 trpc-ret 判断
      // 接口规范：https://git.woa.com/video_pay_baseplat/ff_rule/trpc_http_rule
      (strict && (Number(res.headers['trpc-func-ret']) !== 0 || Number(res.headers['trpc-ret']) !== 0))
      // 兼容低版本——如果body为空即认为请求失败——的逻辑
      || (!strict && !res.data)
    ) {
      throw new RequestError(
        res.headers['trpc-func-ret'] || res.headers['trpc-ret'] || -999,
        res.headers['trpc-error-msg'] || '请求错误',
        res,
        RequestErrorType.LOGIC_ERR,
      );
    }

    return res.data;
  } catch (e) {
    if (e instanceof RequestError) {
      throw e;
    }

    throw new RequestError(
      0,
      '网络开小差了！请重试',
      e,
      RequestErrorType.NETWORK_ERR,
    );
  }
}
